import type { AiReprocessItem } from "@/shared/api/api-client";
import { OUTCOME_LABELS, type ChangeParts } from "@/features/mentions/ai-reprocess-view";
import { formatShortDate } from "@/shared/utils/format";

/**
 * Uma menção de um reprocessamento por IA: de qual matéria se trata e o
 * antes → depois de cada campo. O mesmo desenho na amostra (dentro do
 * diálogo) e no detalhe da execução (`/reprocessamentos/:id`).
 */

/** De qual matéria se trata — o título quando o servidor manda; o id
 *  sozinho só quando não há outro jeito de dizer. */
export function AiReprocessItemHead({ item }: { item: AiReprocessItem }) {
  const title = item.title?.trim();
  const meta = [
    item.channel,
    item.published_at ? formatShortDate(new Date(item.published_at)) : null,
    title ? `#${item.mention_id}` : null
  ].filter(Boolean);
  return (
    <div className="ai-reprocess-card-head">
      <div>
        <strong className="ai-reprocess-card-title">
          {title || `Menção #${item.mention_id}`}
        </strong>
        {meta.length > 0 && (
          <span className="ai-reprocess-card-meta">{meta.join(" · ")}</span>
        )}
      </div>
      {/* "Mudaria" é o caso de quase todo card — o selo só aparece pro
          que foge disso. */}
      {item.outcome !== "previewed" && (
        <span className="ai-reprocess-card-outcome">
          {item.outcome ? OUTCOME_LABELS[item.outcome] : "Pendente"}
        </span>
      )}
    </div>
  );
}

/** O antes → depois de cada campo: o valor atual riscado e o novo em
 *  destaque; listas dizem quantos entram e quantos saem. */
export function AiReprocessChangeList({ parts }: { parts: ChangeParts[] }) {
  if (!parts.length) return null;
  return (
    <dl className="ai-reprocess-changes">
      {parts.map(part => (
        <div key={part.field}>
          <dt>{part.label}</dt>
          <dd>
            {part.before !== undefined && part.after !== undefined ? (
              <>
                <span className="ai-reprocess-before">{part.before}</span>
                <span className="ai-reprocess-arrow" aria-hidden="true">→</span>
                <span className="sr-only">passa a ser</span>
                <span className="ai-reprocess-after">{part.after}</span>
              </>
            ) : part.note ? (
              <span className="ai-reprocess-note">{part.note}</span>
            ) : (
              <>
                {part.added ? (
                  <span className="ai-reprocess-added">
                    +{part.added} {part.added === 1 ? "entra" : "entram"}
                  </span>
                ) : null}
                {part.removed ? (
                  <span className="ai-reprocess-removed">
                    −{part.removed} {part.removed === 1 ? "sai" : "saem"}
                  </span>
                ) : null}
              </>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
